import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Check, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { type KBArticle, knowledgeBaseApi } from '@/api/client';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';

interface ArticleFormData {
  question: string;
  answer: string;
  keywords: string;
  category: string;
  priority: number;
  enabled: boolean;
}

function ArticleModal({
  article,
  onClose,
  onSave,
  isLoading,
}: {
  article?: KBArticle;
  onClose: () => void;
  onSave: (data: ArticleFormData) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState<ArticleFormData>({
    question: article?.question ?? '',
    answer: article?.answer ?? '',
    keywords: article?.keywords?.join(', ') ?? '',
    category: article?.category ?? '',
    priority: article?.priority ?? 0,
    enabled: article?.enabled ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <div className="cyber-card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-cyber-light">
          <h2 className="font-display text-white">
            {article ? 'Редактировать статью' : 'Новая статья'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-xs text-gray-500 uppercase mb-1">Вопрос</label>
            <input
              type="text"
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              className="w-full bg-cyber-dark border border-cyber-light rounded-lg px-3 py-2 text-sm text-white focus:border-neon-cyan focus:outline-none"
              placeholder="Как сбросить пароль?"
              required
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 uppercase mb-1">Ответ</label>
            <textarea
              value={formData.answer}
              onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
              className="w-full bg-cyber-dark border border-cyber-light rounded-lg px-3 py-2 text-sm text-white focus:border-neon-cyan focus:outline-none min-h-[120px]"
              placeholder="Для сброса пароля перейдите в Настройки > Аккаунт > Сбросить пароль..."
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 uppercase mb-1">
                Ключевые слова (через запятую)
              </label>
              <input
                type="text"
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                className="w-full bg-cyber-dark border border-cyber-light rounded-lg px-3 py-2 text-sm text-white focus:border-neon-cyan focus:outline-none"
                placeholder="пароль, сброс, аккаунт"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 uppercase mb-1">Категория</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full bg-cyber-dark border border-cyber-light rounded-lg px-3 py-2 text-sm text-white focus:border-neon-cyan focus:outline-none"
                placeholder="account"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 uppercase mb-1">
                Приоритет (0-100)
              </label>
              <input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                className="w-full bg-cyber-dark border border-cyber-light rounded-lg px-3 py-2 text-sm text-white focus:border-neon-cyan focus:outline-none"
                min={0}
                max={100}
              />
            </div>

            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="w-4 h-4 rounded border-cyber-light bg-cyber-dark text-neon-cyan focus:ring-neon-cyan"
              />
              <label htmlFor="enabled" className="text-sm text-gray-300">
                Статья активна
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="cyber-button">
              Отмена
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="cyber-button cyber-button-primary"
            >
              {isLoading ? <LoadingSpinner size="sm" /> : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function KnowledgeBasePage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editingArticle, setEditingArticle] = useState<KBArticle | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['knowledge-base', { search, page }],
    queryFn: () => knowledgeBaseApi.list({ search: search || undefined, page, limit: 20 }),
  });

  const createMutation = useMutation({
    mutationFn: knowledgeBaseApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-base'] });
      setIsCreating(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<KBArticle> }) =>
      knowledgeBaseApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-base'] });
      setEditingArticle(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: knowledgeBaseApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['knowledge-base'] }),
  });

  const handleSave = (formData: ArticleFormData) => {
    const articleData = {
      question: formData.question,
      answer: formData.answer,
      keywords: formData.keywords
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean),
      category: formData.category || null,
      priority: formData.priority,
      enabled: formData.enabled,
    };

    if (editingArticle) {
      updateMutation.mutate({ id: editingArticle.id, data: articleData });
    } else {
      createMutation.mutate(articleData);
    }
  };

  const handleDelete = (id: number) => {
    if (confirm('Удалить эту статью?')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <Layout title="База знаний">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="База знаний" subtitle="FAQ для автоматических ответов бота">
      {/* Header with search and add button */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Поиск по вопросам..."
            className="w-full bg-cyber-dark border border-cyber-light rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:border-neon-cyan focus:outline-none"
          />
        </div>
        <button
          onClick={() => setIsCreating(true)}
          className="cyber-button cyber-button-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Добавить
        </button>
      </div>

      {/* Articles list */}
      <div className="cyber-card">
        {data?.data.length === 0 ? (
          <div className="p-8 text-center">
            <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">База знаний пуста</p>
            <p className="text-sm text-gray-500">Добавьте первую статью для автоответов</p>
          </div>
        ) : (
          <div className="divide-y divide-cyber-light">
            {data?.data.map((article) => (
              <div key={article.id} className="p-4 hover:bg-cyber-gray/50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`w-2 h-2 rounded-full ${article.enabled ? 'bg-neon-green' : 'bg-gray-500'}`}
                      />
                      <h3 className="text-sm font-medium text-white truncate">{article.question}</h3>
                    </div>
                    <p className="text-sm text-gray-400 line-clamp-2">{article.answer}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {article.category && (
                        <span className="px-2 py-0.5 text-xs rounded bg-cyber-gray text-gray-300">
                          {article.category}
                        </span>
                      )}
                      {article.keywords?.slice(0, 3).map((kw) => (
                        <span
                          key={kw}
                          className="px-2 py-0.5 text-xs rounded bg-neon-cyan/10 text-neon-cyan"
                        >
                          {kw}
                        </span>
                      ))}
                      {article.keywords?.length > 3 && (
                        <span className="text-xs text-gray-500">
                          +{article.keywords.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingArticle(article)}
                      className="p-2 text-gray-400 hover:text-neon-cyan transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(article.id)}
                      className="p-2 text-gray-400 hover:text-neon-red transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {data && data.pagination.total_pages > 1 && (
          <div className="p-4 border-t border-cyber-light flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Страница {data.pagination.page} из {data.pagination.total_pages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="cyber-button text-sm disabled:opacity-50"
              >
                Назад
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= data.pagination.total_pages}
                className="cyber-button text-sm disabled:opacity-50"
              >
                Вперёд
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {(isCreating || editingArticle) && (
        <ArticleModal
          article={editingArticle ?? undefined}
          onClose={() => {
            setIsCreating(false);
            setEditingArticle(null);
          }}
          onSave={handleSave}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </Layout>
  );
}
